import React, { useState, useRef, useMemo } from 'react';
import { X, Printer, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import QRCode from 'react-qr-code';
import JSZip from 'jszip';
import toast from 'react-hot-toast';

const SIZE_PRESETS = [
  { id: 'small', label: 'Small', size: 3, useCase: 'Table tents, small labels' },
  { id: 'medium', label: 'Medium', size: 5, useCase: 'Standard table stickers' },
  { id: 'large', label: 'Large', size: 8, useCase: 'Wall posters, windows' },
  { id: 'xlarge', label: 'Extra Large', size: 12, useCase: 'Standees, boxes, large displays' },
];

// A4 usable area in cm (after 10mm margins)
const A4_WIDTH_CM = 19;
const A4_HEIGHT_CM = 27.7;
const CELL_PADDING_CM = 0.5;

// Preview: scale A4 to fit within the dialog
const PREVIEW_PAGE_W = 480;
const PREVIEW_SCALE = PREVIEW_PAGE_W / A4_WIDTH_CM; // px per cm in preview
const PREVIEW_PAGE_H = Math.round(A4_HEIGHT_CM * PREVIEW_SCALE);

// px-per-cm for high-res download canvas (150 DPI)
const PX_PER_CM = 59;

// CSS px → cm (print: 1cm = 37.795px)
const CSS_PX_PER_CM = 37.795;

// Estimate the extra height each cell needs for labels (in cm)
function getLabelHeightCm(sizeCm, options) {
  let h = 0;
  if (options.showTableName) {
    const fontPx = Math.max(8, sizeCm * 2.5);
    h += (fontPx + 4) / CSS_PX_PER_CM; // font line + 4px margin-top
  }
  if (options.showFloorName) {
    const fontPx = Math.max(7, sizeCm * 2);
    h += fontPx / CSS_PX_PER_CM;
  }
  if (options.showUrl) {
    const fontPx = Math.max(6, sizeCm * 1.5);
    h += (fontPx * 2.5) / CSS_PX_PER_CM; // ~2.5 lines for URL wrapping
  }
  return h;
}

function getGridInfo(sizeCm, count, options = {}) {
  const cellW = sizeCm + CELL_PADDING_CM * 2;
  const labelH = getLabelHeightCm(sizeCm, options);
  const cellH = sizeCm + CELL_PADDING_CM * 2 + labelH;
  const cols = Math.max(1, Math.floor(A4_WIDTH_CM / cellW));
  const rows = Math.max(1, Math.floor(A4_HEIGHT_CM / cellH));
  const perPage = cols * rows;
  const pages = Math.max(1, Math.ceil(count / perPage));
  return { cols, rows, perPage, pages, cellH };
}

// ── Build print HTML with proper page breaks ─────────────────────────────────
function buildPageHtml(svgStrings, codes, preset, options, title, gridInfo) {
  const cellSizeCm = preset.size;
  const cellWCm = cellSizeCm + CELL_PADDING_CM * 2;
  const cellHCm = gridInfo.cellH; // includes label height
  const { perPage, pages } = gridInfo;

  let pagesHtml = '';
  for (let p = 0; p < pages; p++) {
    const start = p * perPage;
    const end = Math.min(start + perPage, codes.length);
    let cellsHtml = '';
    for (let i = start; i < end; i++) {
      const code = codes[i];
      cellsHtml += `
        <div style="width:${cellWCm}cm;height:${cellHCm}cm;display:flex;flex-direction:column;align-items:center;padding:${CELL_PADDING_CM}cm;box-sizing:border-box;overflow:hidden;">
          <div style="width:${cellSizeCm}cm;height:${cellSizeCm}cm;flex-shrink:0;">${svgStrings[i]}</div>
          ${options.showTableName ? `<div style="margin-top:4px;font-size:${Math.max(8, cellSizeCm * 2.5)}px;font-weight:600;text-align:center;">${code.tableName}</div>` : ''}
          ${options.showFloorName ? `<div style="font-size:${Math.max(7, cellSizeCm * 2)}px;color:#666;text-align:center;">${code.floorName}</div>` : ''}
          ${options.showUrl ? `<div style="font-size:${Math.max(6, cellSizeCm * 1.5)}px;color:#999;text-align:center;word-break:break-all;max-width:${cellSizeCm}cm;">${code.qrUrl}</div>` : ''}
        </div>`;
    }
    pagesHtml += `<div class="page${p < pages - 1 ? ' break' : ''}"><div class="grid">${cellsHtml}</div></div>`;
  }

  return `<html>
  <head><title>${title}</title>
  <style>
    @page{size:A4;margin:10mm}
    body{margin:0;padding:0;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif}
    .page{width:19cm;min-height:27.7cm}
    .break{page-break-after:always}
    .grid{display:flex;flex-wrap:wrap;justify-content:flex-start;align-items:flex-start}
    svg{width:100%;height:100%}
  </style></head>
  <body>${pagesHtml}</body></html>`;
}

// ── Render a single QR SVG + labels to a canvas and return as Blob ───────────
function renderQrToCanvas(svgString, code, preset, options) {
  const sizePx = preset.size * PX_PER_CM;
  const padding = 30;
  const labelFontPx = Math.max(14, Math.round(preset.size * 3.5));
  const subFontPx = Math.max(12, Math.round(preset.size * 2.8));
  const urlFontPx = Math.max(10, Math.round(preset.size * 2.2));

  let extraH = padding;
  if (options.showTableName) extraH += labelFontPx + 6;
  if (options.showFloorName) extraH += subFontPx + 4;
  if (options.showUrl) extraH += urlFontPx * 3 + 8; // allow wrapping room

  const canvasW = sizePx + padding * 2;
  const canvasH = sizePx + padding + extraH;

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = canvasW;
      canvas.height = canvasH;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvasW, canvasH);
      ctx.drawImage(img, padding, padding, sizePx, sizePx);

      let labelY = padding + sizePx + 8;
      ctx.textAlign = 'center';
      const cx = canvasW / 2;

      if (options.showTableName) {
        ctx.fillStyle = '#111';
        ctx.font = `600 ${labelFontPx}px -apple-system, sans-serif`;
        ctx.fillText(code.tableName, cx, labelY + labelFontPx);
        labelY += labelFontPx + 6;
      }
      if (options.showFloorName) {
        ctx.fillStyle = '#666';
        ctx.font = `${subFontPx}px -apple-system, sans-serif`;
        ctx.fillText(code.floorName, cx, labelY + subFontPx);
        labelY += subFontPx + 4;
      }
      if (options.showUrl) {
        ctx.fillStyle = '#999';
        ctx.font = `${urlFontPx}px -apple-system, sans-serif`;
        const maxW = sizePx + padding;
        const words = code.qrUrl.split(/(?=[/?.&#])/);
        let line = '';
        for (const w of words) {
          const test = line + w;
          if (ctx.measureText(test).width > maxW && line) {
            ctx.fillText(line, cx, labelY + urlFontPx);
            labelY += urlFontPx + 2;
            line = w;
          } else {
            line = test;
          }
        }
        if (line) ctx.fillText(line, cx, labelY + urlFontPx);
      }

      canvas.toBlob((blob) => resolve(blob), 'image/png');
    };
    img.onerror = () => resolve(null);
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
  });
}

export default function PrintQRDialog({ open, onClose, qrCodes = [], title = 'Print / Download QR Codes' }) {
  const [selectedSize, setSelectedSize] = useState('medium');
  const [showTableName, setShowTableName] = useState(true);
  const [showFloorName, setShowFloorName] = useState(true);
  const [showUrl, setShowUrl] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const qrContainerRef = useRef(null);

  const preset = SIZE_PRESETS.find((p) => p.id === selectedSize);
  const labelOptions = useMemo(() => ({ showTableName, showFloorName, showUrl }), [showTableName, showFloorName, showUrl]);
  const gridInfo = useMemo(() => getGridInfo(preset.size, qrCodes.length, labelOptions), [preset.size, qrCodes.length, labelOptions]);

  // Reset page when grid changes
  useMemo(() => setCurrentPage(0), [gridInfo.perPage, qrCodes.length]);

  // Slice codes for current preview page
  const pageStart = currentPage * gridInfo.perPage;
  const pageCodes = qrCodes.slice(pageStart, pageStart + gridInfo.perPage);

  // Preview cell sizes in px (width-based for columns, height-based for rows)
  const cellSizePx = preset.size * PREVIEW_SCALE;
  const cellPaddingPx = CELL_PADDING_CM * PREVIEW_SCALE;
  const cellWidthPx = cellSizePx + cellPaddingPx * 2;
  const cellHeightPx = gridInfo.cellH * PREVIEW_SCALE; // matches real print height
  const qrRenderSize = Math.max(32, Math.round(cellSizePx * 0.92));
  const labelFontPx = Math.max(8, Math.round(preset.size * PREVIEW_SCALE * 0.14));
  const subFontPx = Math.max(7, Math.round(labelFontPx * 0.85));
  const urlFontPx = Math.max(6, Math.round(labelFontPx * 0.7));

  // Collect serialised SVGs from hidden container
  const collectSvgs = () => {
    if (!qrContainerRef.current) return [];
    const serializer = new XMLSerializer();
    return Array.from(qrContainerRef.current.querySelectorAll('[data-qr-item]')).map((el) => {
      const svg = el.querySelector('svg');
      return svg ? serializer.serializeToString(svg) : '';
    });
  };

  // ── Print ──────────────────────────────────────────────────────────────────
  const handlePrint = () => {
    const svgs = collectSvgs();
    if (!svgs.length) return;
    const html = buildPageHtml(svgs, qrCodes, preset, { showTableName, showFloorName, showUrl }, title, gridInfo);
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };

  // ── Download — single = direct PNG, multiple = ZIP of individual PNGs ──────
  const handleDownload = async () => {
    const svgs = collectSvgs();
    if (!svgs.length) return;
    setDownloading(true);

    const options = { showTableName, showFloorName, showUrl };

    try {
      if (qrCodes.length === 1) {
        const blob = await renderQrToCanvas(svgs[0], qrCodes[0], preset, options);
        if (blob) {
          const link = document.createElement('a');
          link.download = `qr-${qrCodes[0].tableName.replace(/\s+/g, '-').toLowerCase()}.png`;
          link.href = URL.createObjectURL(blob);
          link.click();
          URL.revokeObjectURL(link.href);
          toast.success('QR code downloaded');
        }
      } else {
        const zip = new JSZip();
        for (let i = 0; i < qrCodes.length; i++) {
          const blob = await renderQrToCanvas(svgs[i], qrCodes[i], preset, options);
          if (blob) {
            const name = `qr-${qrCodes[i].tableName.replace(/\s+/g, '-').toLowerCase()}.png`;
            zip.file(name, blob);
          }
        }
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        const link = document.createElement('a');
        link.download = 'qr-codes.zip';
        link.href = URL.createObjectURL(zipBlob);
        link.click();
        URL.revokeObjectURL(link.href);
        toast.success(`${qrCodes.length} QR codes downloaded as ZIP`);
      }
    } catch {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  if (!open || !qrCodes.length) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />

      <div className="relative bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-[600px] mx-4 max-h-[92vh] flex flex-col">
        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="px-5 pt-5 pb-4 border-b border-slate-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 p-1 rounded hover:bg-slate-100 transition-colors"
          >
            <X size={16} className="text-slate-400" />
          </button>

          <div className="flex items-center gap-2.5 mb-1">
            <div className="p-1.5 bg-purple-100 rounded-lg">
              <Printer size={16} className="text-purple-600" />
            </div>
            <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          </div>
          <p className="text-xs text-slate-500 ml-9">
            {qrCodes.length} QR code{qrCodes.length !== 1 ? 's' : ''} selected
          </p>
        </div>

        {/* ── Controls Row ────────────────────────────────────────── */}
        <div className="px-5 py-4 border-b border-slate-100 flex-shrink-0">
          {/* Size Presets */}
          <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Size Preset</label>
          <div className="flex gap-2 mb-4">
            {SIZE_PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedSize(p.id)}
                className={`flex-1 p-2 rounded-lg border text-center transition-all ${
                  selectedSize === p.id
                    ? 'border-purple-400 bg-purple-50 ring-1 ring-purple-200'
                    : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <div className="text-[11px] font-semibold text-slate-900">{p.label}</div>
                <div className="text-[10px] text-slate-500">{p.size}×{p.size} cm</div>
              </button>
            ))}
          </div>

          {/* Options */}
          <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Options</label>
          <div className="flex items-center gap-5">
            {[
              { label: 'Table name', checked: showTableName, onChange: setShowTableName },
              { label: 'Floor name', checked: showFloorName, onChange: setShowFloorName },
              { label: 'QR URL', checked: showUrl, onChange: setShowUrl },
            ].map((opt) => (
              <label key={opt.label} className="flex items-center gap-1.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={opt.checked}
                  onChange={(e) => opt.onChange(e.target.checked)}
                  className="w-3.5 h-3.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                />
                <span className="text-xs text-slate-700">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* ── Preview Area ────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto bg-slate-100 px-5 py-4" style={{ minHeight: 0 }}>
          {/* Page Navigation */}
          {gridInfo.pages > 1 && (
            <div className="flex items-center justify-center gap-3 mb-3">
              <button
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                className="p-1 rounded hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} className="text-slate-600" />
              </button>
              <span className="text-xs font-medium text-slate-600">
                Page {currentPage + 1} of {gridInfo.pages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(gridInfo.pages - 1, p + 1))}
                disabled={currentPage === gridInfo.pages - 1}
                className="p-1 rounded hover:bg-slate-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} className="text-slate-600" />
              </button>
            </div>
          )}

          {/* A4 Page */}
          <div
            className="bg-white rounded shadow-md border border-slate-200 mx-auto"
            style={{
              width: PREVIEW_PAGE_W,
              minHeight: PREVIEW_PAGE_H,
              padding: CELL_PADDING_CM * PREVIEW_SCALE,
            }}
          >
            <div className="flex flex-wrap" style={{ alignItems: 'flex-start' }}>
              {pageCodes.map((code) => (
                <div
                  key={code.id}
                  className="flex flex-col items-center overflow-hidden"
                  style={{
                    width: cellWidthPx,
                    height: cellHeightPx,
                    padding: cellPaddingPx,
                  }}
                >
                  <QRCode
                    value={code.qrUrl}
                    size={qrRenderSize}
                    level="H"
                    style={{ width: cellSizePx, height: cellSizePx }}
                  />
                  {showTableName && (
                    <div
                      className="text-slate-900 font-semibold text-center w-full mt-0.5"
                      style={{ fontSize: labelFontPx, lineHeight: 1.3 }}
                    >
                      {code.tableName}
                    </div>
                  )}
                  {showFloorName && (
                    <div
                      className="text-slate-500 text-center w-full"
                      style={{ fontSize: subFontPx, lineHeight: 1.2 }}
                    >
                      {code.floorName}
                    </div>
                  )}
                  {showUrl && (
                    <div
                      className="text-slate-400 text-center w-full break-all"
                      style={{ fontSize: urlFontPx, lineHeight: 1.2 }}
                    >
                      {code.qrUrl}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Grid info */}
          <p className="text-[10px] text-slate-400 text-center mt-3">
            {gridInfo.cols}×{gridInfo.rows} per page · A4
            {gridInfo.pages > 1 ? ` · ${gridInfo.pages} pages · ${qrCodes.length} total` : ''}
          </p>
        </div>

        {/* Hidden QR render container for print/download serialisation */}
        <div ref={qrContainerRef} style={{ position: 'absolute', left: -9999, top: -9999, width: 1, height: 1, overflow: 'hidden' }}>
          {qrCodes.map((code) => (
            <div key={code.id} data-qr-item data-table={code.tableName} data-floor={code.floorName} data-url={code.qrUrl}>
              <QRCode value={code.qrUrl} size={512} level="H" />
            </div>
          ))}
        </div>

        {/* ── Footer Actions ──────────────────────────────────────── */}
        <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-end gap-2 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-3 py-2 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg border border-purple-300 bg-white text-purple-700 hover:bg-purple-50 disabled:opacity-50 transition-colors"
          >
            <Download size={14} />
            {downloading ? 'Downloading...' : qrCodes.length > 1 ? 'Download ZIP' : 'Download PNG'}
          </button>
          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors"
          >
            <Printer size={14} />
            Print
          </button>
        </div>
      </div>
    </div>
  );
}
